import { filter, type Observable, Subject, switchMap } from 'rxjs'
import type { WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import type { HubSpotTrackingApi } from '@/features/support/api/hubspot-tracking-api'
import { observable, runInAction } from 'mobx'
import { toStream } from '@/utils/mobx-util'
import { map, take } from 'rxjs/operators'

const WIDGET_LOAD_TIMEOUT_MS = 5_000

export class ChatSupportStore {
  private readonly _open$ = new Subject<void>()
  private _widgetBlockedTimeout: ReturnType<typeof setTimeout> | null = null

  /**
   * Whether external support is enabled.
   */
  @observable accessor isSupportEnabled = false

  /**
   * Whether the HubSpot widget failed to load (e.g. blocked by an ad blocker).
   */
  @observable accessor isWidgetBlocked = false

  /**
   * Emits when we want to open the support chat.
   */
  readonly open$ = this._open$.asObservable()

  constructor(
    private currentMember$: Observable<WorkspaceMember>,
    private readonly _api: HubSpotTrackingApi,
  ) {
    this.currentMember$
      .pipe(
        filter(({ user }) => !user.email?.includes('e2e-')),
        filter(({ workspace }) => !workspace.name.includes('e2e-test')),
        switchMap((member) =>
          // The workspace config may not have been fully hydrated yet
          // if the current workspace was hydrated via the server-rendered
          // data. This is because some workspace configs are not public,
          // and for some reason this is one of them. To get around this,
          // we'll wait specifically for the HubSpot chat config to be observed
          // as enabled before we attempt to set it up.
          toStream(
            () => member.workspace.config.hubspotChat.enabled,
            true,
          ).pipe(
            filter(Boolean),
            map(() => member),
          ),
        ),
        take(1),
      )
      .subscribe((member) => this.setupHubSpot(member.user.email))
  }

  setupHubSpot(email: string | null) {
    const hsConversationsSettings = {
      identificationEmail: email ?? '',
      hideNewThreadLink: true,
      identificationToken: '',
    }

    window.hsConversationsSettings = hsConversationsSettings
    const script = document.createElement('script')
    script.src = '//js.hs-scripts.com/20336616.js'
    script.async = true
    script.defer = true
    script.id = 'hs-script-loader'
    document.body.appendChild(script)
    runInAction(() => (this.isSupportEnabled = true))

    this._api
      .getCurrentUserHubSpotTrackingToken(AbortSignal.timeout(30_000))
      .then((token) => {
        hsConversationsSettings.identificationToken = token
      })
      .catch((error) => {
        console.error('Failed to get HubSpot tracking token', error)
      })

    window.hsConversationsOnReady ??= []
    window.hsConversationsOnReady.push(() => {
      if (this._widgetBlockedTimeout) {
        clearTimeout(this._widgetBlockedTimeout)
        this._widgetBlockedTimeout = null
      }
      runInAction(() => (this.isWidgetBlocked = false))
    })

    this._widgetBlockedTimeout = setTimeout(() => {
      if (!window.HubSpotConversations) {
        runInAction(() => (this.isWidgetBlocked = true))
      }
    }, WIDGET_LOAD_TIMEOUT_MS)
  }

  /**
   * Opens the support chat.
   */
  openSupportChat() {
    this._open$.next()
  }
}
